// src/store/slices/managedserviceSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { managedserviceService } from "../../services/api/managedserviceService";
import { IManagedService } from "../../types/managedservice";

interface ManagedServiceState {
  managedservices: IManagedService[];
  selectedManagedService: IManagedService | null;
  loading: boolean;
  selectedManagedServiceLoading: boolean;
  error: string | null;
  selectedManagedServiceError: string | null;
  lastFetched: number | null;
}

const initialState: ManagedServiceState = {
  managedservices: [],
  selectedManagedService: null,
  loading: false,
  selectedManagedServiceLoading: false,
  error: null,
  selectedManagedServiceError: null,
  lastFetched: null,
};

export const getAllManagedServices = createAsyncThunk(
  "managedservices/fetchAll",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { managedservices: ManagedServiceState };
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (
        state.managedservices.lastFetched &&
        now - state.managedservices.lastFetched < fiveMinutes &&
        state.managedservices.managedservices.length > 0
      ) {
        return state.managedservices.managedservices;
      }

      const managedservices =
        await managedserviceService.getAllManagedServices();
      return managedservices;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch managedservices"
      );
    }
  }
);

export const fetchManagedServiceById = createAsyncThunk(
  "managedservices/fetchById",
  async (managedserviceId: string, { rejectWithValue }) => {
    try {
      const managedservice = await managedserviceService.getManagedServiceById(
        managedserviceId
      );
      return managedservice;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch managedservice"
      );
    }
  }
);

const managedserviceSlice = createSlice({
  name: "managedservices",
  initialState,
  reducers: {
    clearSelectedManagedService: (state) => {
      state.selectedManagedService = null;
      state.selectedManagedServiceError = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.selectedManagedServiceError = null;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllManagedServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllManagedServices.fulfilled, (state, action) => {
        state.loading = false;
        state.managedservices = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(getAllManagedServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchManagedServiceById.pending, (state) => {
        state.selectedManagedServiceLoading = true;
        state.selectedManagedServiceError = null;
      })
      .addCase(fetchManagedServiceById.fulfilled, (state, action) => {
        state.selectedManagedServiceLoading = false;
        state.selectedManagedService = action.payload;
        const index = state.managedservices.findIndex(
          (d) => d.managedserviceId === action.payload.managedserviceId
        );
        if (index !== -1) {
          state.managedservices[index] = action.payload;
        }
      })
      .addCase(fetchManagedServiceById.rejected, (state, action) => {
        state.selectedManagedServiceLoading = false;
        state.selectedManagedServiceError = action.payload as string;
      });
  },
});

export const { clearSelectedManagedService, clearErrors, invalidateCache } =
  managedserviceSlice.actions;
export default managedserviceSlice.reducer;
